import React, { useMemo } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  formatInrAmount as formatCurrency,
  formatIndianNumber as formatNumber,
  formatIndianPercent as formatPercent,
  formatCompactInrAxis as formatCompact,
  formatLitres,
} from "../../../utils/reportFormat";

const CHART_COLORS = {
  petrol: "#16A34A",
  diesel: "#2563EB",
  "non-fuel": "#D97706",
  total: "#5D38DB",
  marginPct: "#0D9488",
  default: "#64748B",
};

const resolveSeriesColor = (series) => {
  if (!series) {
    return CHART_COLORS.default;
  }
  if (series.type === "margin_pct") {
    return CHART_COLORS.marginPct;
  }
  if (series.type === "total_margin") {
    return CHART_COLORS.total;
  }
  if (series.type === "non_fuel_margin") {
    return CHART_COLORS["non-fuel"];
  }

  const name = String(series.fuel || series.label || "").toLowerCase();
  if (name.includes("diesel")) {
    return CHART_COLORS.diesel;
  }
  if (name.includes("petrol")) {
    return CHART_COLORS.petrol;
  }

  return CHART_COLORS.default;
};

const MarginTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) {
    return null;
  }

  const rows = [...payload]
    .filter((item) => item.value != null && item.value !== "")
    .sort((a, b) => {
      if (a.dataKey === "Margin %") {
        return 1;
      }
      if (b.dataKey === "Margin %") {
        return -1;
      }
      return (b.value || 0) - (a.value || 0);
    });

  return (
    <div className="owner-chart-tooltip">
      <p className="owner-chart-tooltip__title">{label}</p>
      {rows.map((item) => (
        <div className="owner-chart-tooltip__row" key={item.dataKey}>
          <span
            className="owner-chart-tooltip__dot"
            style={{ backgroundColor: item.color }}
          />
          <span className="owner-chart-tooltip__label">{item.name}</span>
          <span className="owner-chart-tooltip__value">
            {item.dataKey === "Margin %"
              ? formatPercent(item.value)
              : formatCurrency(item.value)}
          </span>
        </div>
      ))}
    </div>
  );
};

const LegendContent = ({ payload }) => {
  if (!payload?.length) {
    return null;
  }

  return (
    <div className="owner-chart-legend">
      {payload.map((entry) => (
        <span className="owner-chart-legend__item" key={entry.value}>
          <span
            className="owner-chart-legend__swatch"
            style={{ backgroundColor: entry.color }}
          />
          {entry.value}
        </span>
      ))}
    </div>
  );
};

const OwnerProfitMarginChart = ({ data = [], series = [], loading = false }) => {
  const marginSeries = useMemo(
    () => series.filter((item) => item.type !== "margin_pct"),
    [series]
  );
  const marginPctSeries = useMemo(
    () => series.find((item) => item.type === "margin_pct"),
    [series]
  );

  const summary = useMemo(() => {
    let periodMargin = 0;
    let marginPctSum = 0;
    let marginPctCount = 0;
    let peakValue = Number.NEGATIVE_INFINITY;
    let peakLabel = "—";
    let lowValue = Number.POSITIVE_INFINITY;
    let lowLabel = "—";
    let activeBuckets = 0;

    data.forEach((point) => {
      const total = Number(point.Total);
      const marginPct = Number(point["Margin %"]);

      if (!Number.isFinite(total)) {
        return;
      }

      periodMargin += total;
      activeBuckets += 1;

      if (Number.isFinite(marginPct)) {
        marginPctSum += marginPct;
        marginPctCount += 1;
      }

      if (total > peakValue) {
        peakValue = total;
        peakLabel = point.name;
      }
      if (total < lowValue) {
        lowValue = total;
        lowLabel = point.name;
      }
    });

    const avgMarginPct =
      marginPctCount > 0 ? marginPctSum / marginPctCount : 0;

    return {
      periodMargin,
      avgMarginPct,
      peakValue: Number.isFinite(peakValue) ? peakValue : 0,
      peakLabel,
      lowValue: Number.isFinite(lowValue) ? lowValue : 0,
      lowLabel,
      activeBuckets,
    };
  }, [data]);

  if (loading) {
    return (
      <div className="owner-chart-empty">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    );
  }

  if (!data.length) {
    return (
      <div className="owner-chart-empty">
        <i className="fa fa-line-chart owner-chart-empty__icon" aria-hidden="true" />
        <p>No margin data for the selected period.</p>
      </div>
    );
  }

  return (
    <div className="owner-sales-chart">
      <div className="owner-sales-kpi row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--brand">
            <span className="owner-sales-kpi__label">Period margin</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.periodMargin)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--teal">
            <span className="owner-sales-kpi__label">Avg margin %</span>
            <strong className="owner-sales-kpi__value">
              {formatPercent(summary.avgMarginPct)}
            </strong>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--green">
            <span className="owner-sales-kpi__label">Best period</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.peakValue)}
            </strong>
            <small className="owner-sales-kpi__hint">{summary.peakLabel}</small>
          </div>
        </div>
        <div className="col-sm-6 col-xl-3">
          <div className="owner-sales-kpi__card owner-sales-kpi__card--amber">
            <span className="owner-sales-kpi__label">Lowest period</span>
            <strong className="owner-sales-kpi__value">
              {formatCurrency(summary.lowValue)}
            </strong>
            <small className="owner-sales-kpi__hint">{summary.lowLabel}</small>
          </div>
        </div>
      </div>

      <div className="owner-chart-panel">
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            data={data}
            margin={{ top: 12, right: 48, left: 4, bottom: 8 }}
          >
            <CartesianGrid stroke="#E8ECF1" strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
              dy={8}
            />
            <YAxis
              yAxisId="left"
              tickFormatter={formatCompact}
              tick={{ fill: "#64748b", fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={72}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={formatPercent}
              tick={{ fill: CHART_COLORS.marginPct, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
              width={56}
            />
            <ReferenceLine yAxisId="left" y={0} stroke="#cbd5e1" strokeDasharray="4 4" />
            <Tooltip content={<MarginTooltip />} />
            <Legend content={<LegendContent />} />

            {marginSeries.map((item) => {
              const color = resolveSeriesColor(item);
              const isTotal = item.type === "total_margin";

              return (
                <Line
                  key={item.key}
                  yAxisId="left"
                  type="monotone"
                  dataKey={item.key}
                  name={item.label}
                  stroke={color}
                  strokeWidth={isTotal ? 2.5 : 2}
                  dot={{ r: isTotal ? 3 : 2, fill: color, strokeWidth: 0 }}
                  activeDot={{
                    r: isTotal ? 5 : 4,
                    fill: color,
                    stroke: "#fff",
                    strokeWidth: 2,
                  }}
                  connectNulls
                />
              );
            })}

            {marginPctSeries ? (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey={marginPctSeries.key}
                name={marginPctSeries.label}
                stroke={CHART_COLORS.marginPct}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={{ r: 2, fill: CHART_COLORS.marginPct, strokeWidth: 0 }}
                activeDot={{
                  r: 4,
                  fill: CHART_COLORS.marginPct,
                  stroke: "#fff",
                  strokeWidth: 2,
                }}
                connectNulls
              />
            ) : null}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default OwnerProfitMarginChart;
